import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FidelityCardDto } from '@proxy/dto-common-website/fidelity-card';
import { CustomerService as ColleagueCustomerService } from "@proxy/user-casto/customers";
import { FidelityCardTypeCh } from '@proxy/cheetah/client/enum';
import { PrintService } from 'src/app/_services/print.service';
import { MODAL, ModalService } from 'src/app/modals/modal.service';
import { ConfigStateService } from '@abp/ng.core';
import { CustomerDto } from '@proxy/dto-common-website/customers';
import { Context } from 'src/app/utils/context';
import { CustomerService } from 'src/publicproxy/src/proxy/public/customer';
import { CaptainWalletService } from 'src/app/_services/captainwallet.service';
import { PaytweakRequestDto } from '@proxy/paytweak-api/dto';
import { getCompleteAddress } from 'src/app/_helpers/common.helper';
import { catchError, EMPTY, finalize, forkJoin, Observable, of, Subject, takeUntil } from 'rxjs';
import { GTM } from 'src/app/_services/gtm.service';

@Component({
  selector: 'ng-casto-common-customer-card-information',
  templateUrl: './customer-card-information.component.html'
})
export class CustomerCardInformationComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() customerId = '';
  @Input() context: Context = Context.Colleague;
  @Input() cardFidelity: FidelityCardDto | null = null;
  @Input() cardEmail: string | null = null;
  @Input() isReadOnly: boolean = false;

  @ViewChild('barcode', { static: false })
  set barcodeRef(el: ElementRef<HTMLDivElement> | undefined) {
    this.barcode = el;
    this.maybeAddBarcode();
  }
  private barcode?: ElementRef<HTMLDivElement>;

  loading: boolean;
  customerInfos!: CustomerDto;
  protected readonly FidelityCardType = FidelityCardTypeCh;

  private readonly destroy$ = new Subject<void>();
  private viewReady = false;

  constructor(
    private readonly colleagueCustomerService: ColleagueCustomerService,
    private readonly customerService: CustomerService,
    private readonly modalService: ModalService,
    private readonly router: Router,
    private readonly print: PrintService,
    private readonly captainWalletService: CaptainWalletService,
    public config: ConfigStateService,
    private gtm: GTM) { }

  ngOnInit() {
    this.loading = true;

    if (this.cardFidelity && this.customerInfos) {
      this.maybeAddBarcode();
      this.loading = false;
      return;
    }

    let obs: Observable<{ card: FidelityCardDto; infos: CustomerDto; }>;

    if (this.context === Context.Colleague) {
      obs = forkJoin({
        card: this.cardFidelity ? of(this.cardFidelity) : this.colleagueCustomerService.getFidelityCard(this.customerId),
        infos: this.colleagueCustomerService.getCustomer(this.customerId)
      });
    } else {
      obs = forkJoin({
        card: this.cardFidelity ? of(this.cardFidelity) : this.customerService.getFidelityCard(),
        infos: this.customerService.getCustomer()
      });
    }

    obs
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => {
          console.error("Une erreur est survenue pendant le chargement des infos", err);
          if (this.context === Context.Customer) {
            this.router.navigate(['/creation/']);
          }
          return EMPTY;
        }),
        finalize(() => this.loading = false)
      )
      .subscribe(({ card, infos }) => {
        this.cardFidelity = card;
        this.customerInfos = infos;
        this.maybeAddBarcode();
      });
  }

  ngAfterViewInit() {
    this.viewReady = true;
    this.maybeAddBarcode();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private maybeAddBarcode(): void {
    if (!this.viewReady) return;
    if (!this.cardFidelity?.cardNumber) return;
    this.addBarcode();
  }

  private addBarcode(): void {
    const host = this.barcode?.nativeElement;
    if (!host) return;

    host.innerHTML = '';

    const div = document.createElement("div");
    div.innerHTML = this.print.createBarCode(this.cardFidelity.cardNumber);
    host.appendChild(div);
  }

  goToUpgradeCard() {
    this.gtm.trackInteraction("passage_max");
    this.router.navigate(['/clients/carte/' + this.customerId + '/modification'], { queryParams: { upgrade: true } });
  }

  activateCard() {
    this.modalService.display(MODAL.ACTIVATE_CARD_CONFIRMATION, {
      cardNumber: this.cardFidelity?.cardNumber,
      customerId: this.customerId,
      context: this.context
    });
  }

  displayRenewBtn(): boolean {
    if (!this.cardFidelity || !this.customerInfos) return false;

    let today = new Date();

    //Conversion de la date d'expiration en Date javascript
    let parts = this.cardFidelity.finalExpirationDate.split('/');
    let expirationDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));

    let dateToNotice = new Date();

    //Ajout de la date de notification en fonction du setting
    dateToNotice.setTime(expirationDate.getTime() - (parseInt(this.config.getSetting('CastoramaFidelite.NbDaysNoticeEndSubscriptionPaidCard')) * 24 * 60 * 60 * 1000))

    return today > dateToNotice;
  }

  displayPayCardBtn(): boolean {
    if (!this.cardFidelity || !this.customerInfos) return false;
    if (this.customerInfos.isColleague) return false;

    return this.cardFidelity.type === FidelityCardTypeCh.PremiumWaiting && !this.customerInfos.cardIsPaidButNotActiveYet;
  }

  payCard() {
    this.modalService.display(MODAL.PAYMENT_METHOD, {
      context: this.context,
      paytweakPayload: this.getPaytweakPayload(),
      fidelityCard: this.cardFidelity
    },
      () => {
        if (this.modalService.returnValue) {
          window.location.reload();
        }
      });
  }

  cancelCard() {
    this.modalService.display(MODAL.CANCEL_CARD_CONFIRMATION, {
      cardNumber: this.cardFidelity?.cardNumber,
      customerId: this.customerId,
      context: this.context
    });
  }

  printCard() {
    this.modalService.display(MODAL.DISPLAY_BARCODE, {
      barcodeValue: this.cardFidelity?.cardNumber,
      icon: 'assets/img/icons.svg#icon-creation-carte',
      title: 'Carte C-Casto' + (this.cardFidelity?.type !== FidelityCardTypeCh.Gratuite ? ' Max' : ''),
      subtitle: this.getCardValidityLabel()
    });
  }

  /**
   * Seule la carte payante (C-Casto Max) expire. Une carte gratuite peut conserver la date
   * d'expiration de son ancien abonnement Max : il ne faut alors pas l'afficher. La carte
   * des collègues n'expire pas non plus.
   */
  displayExpirationDate(): boolean {
    return this.cardFidelity?.type === FidelityCardTypeCh.Premium
      && !!this.cardFidelity?.finalExpirationDate
      && !this.customerInfos?.isColleague;
  }

  /**
   * Mention de validité affichée sous le titre de la carte, à l'écran comme à l'impression.
   */
  private getCardValidityLabel(): string {
    return this.displayExpirationDate() ? `Valable jusqu'au ${this.cardFidelity.finalExpirationDate}` : '';
  }

  renewCard() {

    if (this.context === Context.Colleague) {
      this.colleagueCustomerService.renewPremiumCard({ customerKfId: this.cardFidelity.customerId }).subscribe({
        next: () => {
          this.modalService.display(MODAL.PAYMENT_METHOD, {
            context: this.context,
            paytweakPayload: this.getPaytweakPayload(),
            fidelityCard: this.cardFidelity
          }, () => window.location.reload());
        }
      });
    } else {
      this.customerService.renewPremiumCard().subscribe({
        next: () => {
          this.modalService.display(MODAL.PAYMENT_METHOD, {
            context: this.context,
            paytweakPayload: this.getPaytweakPayload(),
            fidelityCard: this.cardFidelity
          }, () => window.location.reload());
        }
      });
    }
  }

  downgradeCard() {
    this.colleagueCustomerService.forceColleagueFreeCard({ customerKfId: this.cardFidelity.customerId }).subscribe({
      next: () => {
        this.modalService.display(MODAL.SUCCESS, {
          title: 'C-Casto',
          message: 'Vous êtes bien passé sur une carte C-Casto.'
        }, () => window.location.reload());
      }
    });
  }

  isCustomer(): boolean {
    return this.context === Context.Customer;
  }

  public addToWallet(): void {
    window.open(this.captainWalletService.generateUrl(this.cardFidelity.captainWalletUserId, 'home'), '_blank').focus();
  }

  /**
   * Le QR code wallet n'a de sens que si CaptainWallet est configuré sur l'environnement,
   * que la carte est active et que le client possède un identifiant CaptainWallet :
   * sans eux, l'URL générée serait invalide.
   */
  canAddToWallet(): boolean {
    return this.captainWalletService.isConfigured()
      && !!this.cardFidelity?.cardEnabled
      && !!this.cardFidelity?.captainWalletUserId;
  }

  /**
   * Affiche le QR code CaptainWallet dans une modale.
   * Le poste vendeur ne peut pas ajouter la carte au Wallet à la place du client :
   * le vendeur fait scanner ce QR code au client, qui ajoute lui-même sa carte
   * au Wallet de son smartphone.
   */
  showWalletQrCode(): void {
    this.modalService.display(MODAL.ADD_TO_WALLET, {
      walletUrl: this.captainWalletService.generateUrl(this.cardFidelity.captainWalletUserId, 'home'),
      title: 'Ajouter au Wallet du client'
    });
  }

  private getPaytweakPayload(): PaytweakRequestDto {
    if (!this.cardFidelity || !this.customerInfos) return undefined;

    let dto: PaytweakRequestDto = {
      customerId: this.customerId,
      cardNumber: this.cardFidelity.cardNumber,
      address: getCompleteAddress(this.customerInfos.houseNumber, this.customerInfos.address),
      city: this.customerInfos.city,
      country: this.customerInfos.country,
      email: this.customerInfos.mail,
      phone: this.customerInfos.phoneNumber,
      firstname: this.customerInfos.firstName,
      lastname: this.customerInfos.name,
      postalCode: this.customerInfos.postalCode
    };

    return dto;
  }
}
